import React from "react";
import { Form, Typography, message } from "antd";
import PageLoader from "./PageLoader";
import { gql, useMutation } from "@apollo/client";
import { useNavigate, Navigate } from "react-router-dom";
import UserForm from "./UserForm";
import { useCurrentUser } from "../lib/useCurrentUser";
import { USERS_QUERY } from "./UserList";

const { Title } = Typography;

const CREATE_USER_MUTATION = gql`
  mutation CreateUser(
    $firstName: String!
    $lastName: String!
    $email: String!
    $calendarLink: String
    $active: Boolean
    $permissionIds: [String!]
  ) {
    createUser(
      firstName: $firstName
      lastName: $lastName
      email: $email
      calendarLink: $calendarLink
      active: $active
      permissionIds: $permissionIds
    ) {
      id
      firstName
      lastName
      email
      calendarLink
      active
      permissions {
        id
        description
      }
    }
  }
`;

const UserCreate: React.FC = () => {
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [messageApi, contextHolder] = message.useMessage();

  const { hasPermission, loading: currentUserLoading } = useCurrentUser();

  const [createUser, { loading: saving }] = useMutation(CREATE_USER_MUTATION, {
    refetchQueries: [{ query: USERS_QUERY }],
    awaitRefetchQueries: true,
    onCompleted: (data) => {
      messageApi.success(
        `User ${data.createUser.firstName} ${data.createUser.lastName} created successfully`,
      );
      navigate("/users");
    },
    onError: (err) => {
      if (/email|domain/i.test(err.message)) {
        form.setFields([{ name: "email", errors: [err.message] }]);
      } else {
        messageApi.error(err.message);
      }
    },
  });

  if (currentUserLoading)
    return <PageLoader style={{ display: "block", marginTop: 40 }} />;

  if (!hasPermission("users.manage")) return <Navigate to="/users" replace />;

  const onFinish = (values: any) => {
    createUser({
      variables: {
        firstName: values.firstName,
        lastName: values.lastName,
        email: values.email,
        calendarLink: values.calendarLink || null,
        active: values.active ?? false,
        permissionIds: values.permissionIds ?? [],
      },
    });
  };

  return (
    <div style={{ maxWidth: 600 }}>
      {contextHolder}
      <Title level={3} style={{ marginBottom: 24 }}>
        Create User
      </Title>

      <Form
        form={form}
        layout="vertical"
        onFinish={onFinish}
        initialValues={{ active: false, permissionIds: [] }}
      >
        <UserForm
          form={form}
          saving={saving}
          submitLabel="Create User"
          onCancel={() => navigate("/users")}
        />
      </Form>
    </div>
  );
};

export default UserCreate;
